import { Injectable } from "@angular/core";
import { ProductDetails, ProductView } from "falcon-api";
import { BehaviorSubject, Observable } from "rxjs";
import { Account, AccountType } from "src/app/signup/account-selector/account-selector.component";
import { PRODUCT_APY_TEXT_SETTING_KEY, PRODUCT_DESCRIPTION_SETTING_KEY, PRODUCT_TISA_URL_SETTING_KEY } from "../constants";
import { ProductType } from "src/app/signup/models/product-type";

@Injectable({
  providedIn: 'root'
})
export class AccountMediatorService {
  private _fullAccountTypes: AccountType[] = [];
  private _selectedAccounts: Account[] = [];

  private _isJointApplication = false;

  private _selectableAccountTypes$: BehaviorSubject<AccountType[]> = new BehaviorSubject<AccountType[]>([]);
  selectableAccountTypes$: Observable<AccountType[]> = this._selectableAccountTypes$.asObservable();

  constructor() {}

  processAccountConfiguration(productView: ProductView[], productSettings: Map<string, Map<string, unknown>>): void {
    this._fullAccountTypes = productView.map(record => ({
      label: record.accountType!,
      value: record.accountType!,
      accounts: record.products!.map(product => this._createAccountSettings(product, productSettings.get(product.productUID!)))
    }));

    this._sendSelectableAccountTypes();
  }

  updateAccountSettings(productUID: string, productSettings: Map<string, unknown> | undefined): void {
    if (!productSettings) { return; }

    const accountType = this._fullAccountTypes.find(type => type.accounts.find(({value}) => value === productUID));
    const account = accountType?.accounts.find(({ value }) => value === productUID);

    if (!account) { return; }

    account.observations = productSettings.get(PRODUCT_DESCRIPTION_SETTING_KEY) as string;
    
    const apyOverride = productSettings.get(PRODUCT_APY_TEXT_SETTING_KEY) as string;
    if (apyOverride) {
      account.APYText = apyOverride;
    }
    
    const tisaUrl = productSettings.get(PRODUCT_TISA_URL_SETTING_KEY) as string;
    if (tisaUrl) {
      account.TISA = tisaUrl;
    }

    this._sendSelectableAccountTypes();
  }
  
  private _sendSelectableAccountTypes(): void {
    this._fullAccountTypes.forEach(type => {
      if (type.accounts[0].productType !== ProductType.Time) { return; }

      try {
        type.accounts.sort((a, b) => {
          const num1 = Number(a.label.split(" ")[1]);
          const num2 = Number(b.label.split(" ")[1]);
          return num1 < num2 ? -1 : num1 > num2 ? 1 : 0;
        });
      } catch {
        // In case of error do nothing, it'll just not sort
      }
    });

    this._selectableAccountTypes$.next(
      this._removeSelectedAccounts(this._fullAccountTypes, this._selectedAccounts)
    );
  }

  private _createAccountSettings(product: ProductDetails, productSettings: Map<string, unknown> | undefined): Account {
    const {
      name, code, accountType, productUID, apy, allowJointApplications,
      descriptors, maxNumberOfAccounts, productType
    } = product;

    let observations: string = "";
    let APYText: string = "";
    let TISA: string = "";

    if (productSettings) {
      observations = productSettings.get(PRODUCT_DESCRIPTION_SETTING_KEY) as string;
      APYText = productSettings.get(PRODUCT_APY_TEXT_SETTING_KEY) as string;
      TISA = productSettings.get(PRODUCT_TISA_URL_SETTING_KEY) as string;
    }

    return {
      label: name!,
      code: code!.toString(),
      type: accountType!,
      productType: productType! as ProductType,
      value: productUID!,
      APY: apy?.toString() || "",
      APYText,
      allowJointApplications: !!allowJointApplications,
      observations,
      features: !!descriptors ? descriptors.split("|") : [],
      TISA: TISA || "",
      maxNumberOfAccounts
    }


  }

  accountOwnershipChanged(isJointApplication: boolean): void {
    let accountTypes: AccountType[] = JSON.parse(JSON.stringify(this._fullAccountTypes));
    this._isJointApplication = isJointApplication;

    if (isJointApplication) {
      accountTypes = this._filterJointAllowedAccounts(accountTypes);
    }

    this._selectableAccountTypes$.next(
      this._removeSelectedAccounts(accountTypes, this._selectedAccounts)
    );
  }

  accountAdded(account: Account): void {
    if (!account) { return; }

    this._selectedAccounts.push(account);

    if (this._getAccountSelectedCount(account) < account.maxNumberOfAccounts!) {
      return;
    }

    this._selectableAccountTypes$.next(
      this._removeSelectedAccounts(this._fullAccountTypes, this._selectedAccounts)
    );
  }

  private _getAccountSelectedCount(account: Account): number {
    return this._selectedAccounts.filter(selectedAccount => selectedAccount.value === account.value).length;
  }

  accountEdited(previousAccount: string, newAccount: Account): void {
    if (!previousAccount || !newAccount) { return; }

    const accountIndex = this._selectedAccounts.findIndex(value => value.value === previousAccount);
    this._selectedAccounts[accountIndex] = newAccount;

    this._selectableAccountTypes$.next(
      this._removeSelectedAccounts(this._fullAccountTypes, this._selectedAccounts)
    );
  }

  accountDeleted(account: Account): void {
    if (!account) { return; }

    const accountSelectedCount = this._getAccountSelectedCount(account);

    const accountIndex = this._selectedAccounts.findIndex(value => value.value === account.value);
    this._selectedAccounts.splice(accountIndex, 1);

    if (accountSelectedCount >= account.maxNumberOfAccounts!) {
      this._selectableAccountTypes$.next(
        this._removeSelectedAccounts(this._fullAccountTypes, this._selectedAccounts)
      );
    }
  }

  resetAccountState(): void {
    this._selectedAccounts = [];
    this.accountOwnershipChanged(this._isJointApplication);
  }

  private _filterJointAllowedAccounts(selectableAccountTypes: AccountType[]): AccountType[] {
    const accountTypes: AccountType[] = JSON.parse(JSON.stringify(selectableAccountTypes));
    accountTypes.forEach(type => {
      type.accounts = type.accounts.filter(account => account.allowJointApplications)
    });

    return accountTypes;
  }

  private _removeSelectedAccounts(selectableAccountTypes: AccountType[], selectedAccounts: Account[]): AccountType[] {
    let accountTypes: AccountType[] = JSON.parse(JSON.stringify(selectableAccountTypes));

    selectedAccounts.forEach(account => {
      const accountType = accountTypes.find(type =>
        type.accounts.find(value => value.value === account.value)
      );

      const accountIndex = accountType?.accounts.findIndex(value => value.value === account.value);

      if (this._getAccountSelectedCount(account) + 1 > account.maxNumberOfAccounts!) {
        accountType?.accounts.splice(accountIndex!, 1);
      }
    })

    return accountTypes;
  }
}
